'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef } from 'react';
import { useSession } from '@/lib/auth-client';
import { useAddAttendance, useRemoveAttendance } from '@/lib/hooks/useAttendance';
import { useObjectActivities } from '@/lib/hooks/useObjectActivities';
import { useEngagementSummary } from '@/lib/hooks/useEngagementSummary';
import { Ticket, CheckCircle2, Hourglass, ShieldX, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useSignInModal } from '@/lib/ui';
import type { AttendanceSettings } from '@sizzlenz/shared';
interface RegistrationBlockProps {
  objectId: string;
  objectType: 'initiative';
  attendanceSettings: AttendanceSettings;
  attendanceCount?: number;
}
export function RegistrationBlock(t0) {
  const $ = _c(70);
  const {
    objectId,
    objectType,
    attendanceSettings,
    attendanceCount
  } = t0;
  const {
    data: session
  } = useSession();
  let t1;
  if ($[0] !== session?.user?.id) {
    t1 = session?.user?.id?.toString();
    $[0] = session?.user?.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const currentUserId = t1;
  const {
    showSignInModal
  } = useSignInModal();
  const pendingActionRef = useRef(false);
  const {
    data: t2
  } = useObjectActivities(objectId, objectType);
  let t3;
  if ($[2] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const activities = t3;
  const {
    data: engagementData
  } = useEngagementSummary(objectId, objectType);
  const addAttendance = useAddAttendance();
  const removeAttendance = useRemoveAttendance();
  let t4;
  if ($[4] !== activities || $[5] !== currentUserId) {
    let t5;
    if ($[7] !== currentUserId) {
      t5 = a => a.type === "attendance" && String(a.actorId) === currentUserId && !a.deletedAt;
      $[7] = currentUserId;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = activities.find(t5);
    $[4] = activities;
    $[5] = currentUserId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const existingAttendance = t4;
  const isAttending = !!existingAttendance;
  const attendanceStatus = (existingAttendance?.data as any)?.status || null;
  const currentCount = engagementData?.engagement?.attendance?.registered?.count ?? attendanceCount ?? 0;
  const capacity = attendanceSettings.capacity;
  const isFull = capacity != null && currentCount >= capacity;
  const spotsRemaining = capacity != null ? Math.max(0, capacity - currentCount) : null;
  const {
    requiresApproval,
    blockHeading,
    blockDescription,
    buttonLabel
  } = attendanceSettings;
  const heading = blockHeading || "Secure Your Spot";
  const description = blockDescription || (requiresApproval ? "Places are limited and allocated by the host. Submit your details to be considered." : "Grab your spot \u2014 sign up takes just a moment.");
  const ctaLabel = buttonLabel || (requiresApproval ? "Apply to Attend" : "Count Me In");
  const isPending = addAttendance.isPending || removeAttendance.isPending;
  let t5;
  if ($[9] !== addAttendance || $[10] !== currentUserId || $[11] !== isFull || $[12] !== objectId || $[13] !== objectType || $[14] !== requiresApproval || $[15] !== showSignInModal) {
    t5 = async () => {
      if (!currentUserId) {
        pendingActionRef.current = true;
        showSignInModal({
          description: <div className="p-4"><p className="text-base font-medium mb-1">Sign in to continue</p><p className="text-sm text-gray-500">{requiresApproval ? "You will need to be signed in before the host can review your application." : "Quick sign-in and your spot is locked in."}</p></div>,
          preferFullscreenForNewUsers: false,
          onSuccess: () => {
            if (!pendingActionRef.current) {
              return;
            }
            pendingActionRef.current = false;
            addAttendance.mutate({
              objectId,
              objectType
            }, {
              onSuccess: () => {
                toast.success(requiresApproval ? "Application submitted \u2014 the host will be in touch" : "All set \u2014 you are on the list");
              }
            });
          }
        });
        return;
      }
      if (isFull) {
        toast.error("No more spots available");
        return;
      }
      await addAttendance.mutateAsync({
        objectId,
        objectType
      }, {
        onSuccess: () => {
          toast.success(requiresApproval ? "Application submitted \u2014 the host will be in touch" : "All set \u2014 you are on the list");
        }
      });
    };
    $[9] = addAttendance;
    $[10] = currentUserId;
    $[11] = isFull;
    $[12] = objectId;
    $[13] = objectType;
    $[14] = requiresApproval;
    $[15] = showSignInModal;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleRegister = t5;
  let t6;
  if ($[17] !== currentUserId || $[18] !== objectId || $[19] !== objectType || $[20] !== removeAttendance) {
    t6 = async () => {
      if (!currentUserId) {
        return;
      }
      await removeAttendance.mutateAsync({
        actorId: currentUserId,
        objectId,
        objectType
      });
      toast.success("Spot released");
    };
    $[17] = currentUserId;
    $[18] = objectId;
    $[19] = objectType;
    $[20] = removeAttendance;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const handleCancel = t6;
  if (isAttending) {
    let t7;
    if ($[22] !== attendanceStatus) {
      t7 = attendanceStatus === "pending_approval" && <div className="flex gap-3"><div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><Hourglass className="w-5 h-5 text-amber-600" /></div><div><p className="font-semibold text-gray-900 text-base">Under Review</p><p className="text-sm text-gray-500 mt-0.5">The host is reviewing your application. Hang tight.</p></div></div>;
      $[22] = attendanceStatus;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    let t8;
    if ($[24] !== attendanceStatus) {
      t8 = attendanceStatus === "approved" && <div className="flex gap-3"><div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div><div><p className="font-semibold text-gray-900 text-base">Confirmed</p><p className="text-sm text-gray-500 mt-0.5">Your spot is locked in. See you there.</p></div></div>;
      $[24] = attendanceStatus;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    let t9;
    if ($[26] !== attendanceStatus) {
      t9 = attendanceStatus === "declined" && <div className="flex gap-3"><div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center"><ShieldX className="w-5 h-5 text-red-600" /></div><div><p className="font-semibold text-gray-900 text-base">Not Selected</p><p className="text-sm text-gray-500 mt-0.5">Unfortunately the host was unable to accommodate your application this time.</p></div></div>;
      $[26] = attendanceStatus;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    let t10;
    if ($[28] !== attendanceStatus) {
      t10 = attendanceStatus === "registered" && <div className="flex gap-3"><div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center"><Ticket className="w-5 h-5 text-indigo-600" /></div><div><p className="font-semibold text-gray-900 text-base">You're On the List</p><p className="text-sm text-gray-500 mt-0.5">Your attendance is recorded. Looking forward to having you.</p></div></div>;
      $[28] = attendanceStatus;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    let t11;
    if ($[30] !== session) {
      t11 = session?.user && <div className="flex items-center gap-2 pt-2 border-t border-gray-100">{session.user.image ? <img src={session.user.image} alt="" className="w-7 h-7 rounded-full object-cover" /> : <div className="w-7 h-7 rounded-full bg-gray-200" />}<span className="text-sm text-gray-700">{session.user.name || session.user.email}</span></div>;
      $[30] = session;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    let t12;
    if ($[32] !== attendanceStatus || $[33] !== handleCancel || $[34] !== isPending) {
      t12 = attendanceStatus !== "declined" && <button onClick={handleCancel} disabled={isPending} className="text-sm text-gray-400 hover:text-red-500 transition-colors cursor-pointer">Withdraw</button>;
      $[32] = attendanceStatus;
      $[33] = handleCancel;
      $[34] = isPending;
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    let t13;
    if ($[36] !== t10 || $[37] !== t11 || $[38] !== t12 || $[39] !== t7 || $[40] !== t8 || $[41] !== t9) {
      t13 = <div className="rounded-xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 overflow-hidden shadow-sm"><div className="p-5 space-y-4">{t7}{t8}{t9}{t10}{t11}{t12}</div></div>;
      $[36] = t10;
      $[37] = t11;
      $[38] = t12;
      $[39] = t7;
      $[40] = t8;
      $[41] = t9;
      $[42] = t13;
    } else {
      t13 = $[42];
    }
    return t13;
  }
  let t7;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center"><Ticket className="w-5 h-5 text-indigo-600" /></div>;
    $[43] = t7;
  } else {
    t7 = $[43];
  }
  let t8;
  if ($[44] !== heading) {
    t8 = <p className="font-semibold text-gray-900 text-base">{heading}</p>;
    $[44] = heading;
    $[45] = t8;
  } else {
    t8 = $[45];
  }
  let t9;
  if ($[46] !== requiresApproval) {
    t9 = requiresApproval && <p className="text-sm text-indigo-600 mt-0.5">Host-curated attendance</p>;
    $[46] = requiresApproval;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  let t10;
  if ($[48] !== t8 || $[49] !== t9) {
    t10 = <div className="flex items-start gap-3">{t7}<div className="min-w-0">{t8}{t9}</div></div>;
    $[48] = t8;
    $[49] = t9;
    $[50] = t10;
  } else {
    t10 = $[50];
  }
  let t11;
  if ($[51] !== capacity || $[52] !== currentCount || $[53] !== isFull || $[54] !== spotsRemaining) {
    t11 = capacity != null && <div className="flex items-center gap-2 text-sm"><Users className="w-4 h-4 text-gray-400" />{isFull ? <span className="text-red-600 font-medium">Fully booked</span> : spotsRemaining != null && spotsRemaining <= 10 ? <span className="text-orange-600 font-medium">{spotsRemaining} of {capacity} spots open</span> : <span className="text-gray-500">{currentCount} / {capacity} spots taken</span>}</div>;
    $[51] = capacity;
    $[52] = currentCount;
    $[53] = isFull;
    $[54] = spotsRemaining;
    $[55] = t11;
  } else {
    t11 = $[55];
  }
  let t12;
  if ($[56] !== description) {
    t12 = <p className="text-gray-600 text-base leading-relaxed">{description}</p>;
    $[56] = description;
    $[57] = t12;
  } else {
    t12 = $[57];
  }
  const t13 = isPending || isFull;
  const t14 = isPending ? "Processing..." : isFull ? "No Spots Available" : ctaLabel;
  let t15;
  if ($[58] !== handleRegister || $[59] !== t13 || $[60] !== t14) {
    t15 = <button onClick={handleRegister} disabled={t13} className="w-full py-3 rounded-lg text-white font-medium text-base bg-indigo-600 hover:bg-indigo-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-sm">{t14}</button>;
    $[58] = handleRegister;
    $[59] = t13;
    $[60] = t14;
    $[61] = t15;
  } else {
    t15 = $[61];
  }
  let t16;
  if ($[62] !== currentUserId) {
    t16 = !currentUserId && <p className="text-xs text-gray-400 text-center">You will be asked to sign in or create an account</p>;
    $[62] = currentUserId;
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  let t17;
  if ($[64] !== t10 || $[65] !== t11 || $[66] !== t12 || $[67] !== t15 || $[68] !== t16) {
    t17 = <div className="rounded-xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 overflow-hidden shadow-sm"><div className="p-5 space-y-4">{t10}{t11}{t12}{t15}{t16}</div></div>;
    $[64] = t10;
    $[65] = t11;
    $[66] = t12;
    $[67] = t15;
    $[68] = t16;
    $[69] = t17;
  } else {
    t17 = $[69];
  }
  return t17;
}